import { useCustom, useTranslate } from '@refinedev/core';
import { Tag, Typography } from 'antd';

/**
 * E21 · PortalExpositionHeartRate — "Situation Editor — Graph & History — Heart rate (section)" (cc-tools-2; founder
 * Q-e43g1-3 C): the client's heart rate during ONE exposition — lowest, average, highest and the number of readings —
 * only while the client shares heart rate with THIS therapist; otherwise "not shared". "No heart rate was recorded" when
 * the client shares it but the watch recorded none. Numbers only; no download.
 */
type Row = {
  expositionId: string;
  sharedTypes: string[];
  window: { bpm: { min: number; max: number; mean: number; samples: number } } | null;
};

export const ExpositionHeartRateCell = ({ subject, expositionId }: { subject: string; expositionId: string }) => {
  const t = useTranslate();
  const { data } = useCustom<Row>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'portalExpositionHeartRate',
      fields: ['expositionId', 'sharedTypes', { window: [{ bpm: ['min', 'max', 'mean', 'samples'] }] }],
      variables: {
        subject: { name: 'subject', type: 'ID!', value: subject },
        expositionId: { name: 'expositionId', type: 'ID!', value: expositionId },
      },
    },
    queryOptions: { enabled: Boolean(subject && expositionId), retry: false },
  });
  const row = data?.data;
  if (!row) return null;
  if (!row.sharedTypes.includes('heartRate')) {
    return <Tag data-testid={`cc-exposure-history-heart-rate-not-shared-${expositionId}`}>{t('shared.exposure.heartRate.notShared')}</Tag>;
  }
  const hr = row.window?.bpm;
  return (
    <Typography.Text data-testid={`cc-exposure-history-heart-rate-${expositionId}`} type={hr ? undefined : 'secondary'}>
      {hr
        ? t('shared.exposure.heartRate.values', {
            min: hr.min,
            max: hr.max,
            mean: hr.mean,
            readings: t('shared.exposure.heartRate.readings', { count: hr.samples }),
          })
        : t('shared.exposure.heartRate.none')}
    </Typography.Text>
  );
};
